import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { findPeople, shareProject, type Colleague, type ShareResult } from '@shared/services'
import { CloseMark } from './CloseMark'
import { readUi } from '../uiPrefs'
import './share.css'

/**
 * Choosing who gets a copy of a project.
 *
 * Sharing here is a copy, not a shared space: each person chosen gets the
 * project exactly as it is now, and from then on it is theirs. The dialog
 * says so in its first line, because "share" in most software means the
 * other thing, and a person who expects to see their colleague's next
 * changes would otherwise wait for them.
 */
export function ShareDialog({
  projectId,
  title,
  look,
  onClose,
}: {
  projectId: string
  title: string
  /**
   * The card's colour and background picture, sent with the copy (2026-10-26).
   * Not given (from inside the project): the one kept with this person's
   * settings, as the overview shows it; none there, the Worker uses the look
   * the project itself came with.
   */
  look?: { colour?: string; picture?: string } | null
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<Colleague[] | null>(null)
  const [chosen, setChosen] = useState<Colleague[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState<ShareResult | null>(null)
  const input = useRef<HTMLInputElement | null>(null)

  // Everyone to begin with, narrowed as it is typed into.
  useEffect(() => {
    let live = true
    const timer = window.setTimeout(() => {
      findPeople(query)
        .then((people) => {
          if (live) setFound(people)
        })
        .catch((caught: unknown) => {
          if (live) setError(caught instanceof Error ? caught.message : 'Could not look people up')
        })
    }, 180)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [query])

  useEffect(() => {
    input.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const isChosen = (person: Colleague) => chosen.some((one) => one.id === person.id)
  const toggle = (person: Colleague) =>
    setChosen((was) => (isChosen(person) ? was.filter((one) => one.id !== person.id) : [...was, person]))

  const share = async () => {
    if (busy || chosen.length === 0) return
    setBusy(true)
    setError('')
    try {
      const kept =
        look !== undefined
          ? look
          : readUi<Record<string, { colour?: string; picture?: string }>>(
              'ww.overview.projectLooks',
              {},
              (value) => Boolean(value) && typeof value === 'object',
            )[projectId]
      // An empty one is sent as it is: a look taken off on purpose, not to be
      // replaced by the one the project came with.
      setDone(await shareProject(projectId, chosen.map((one) => one.id), kept ?? null))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'It could not be shared')
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div
      className="share"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose()
      }}
    >
      <div className="share__card" role="dialog" aria-modal="true" aria-label={`Share ${title}`}>
        <header className="share__head">
          <h2 className="share__title">Share “{title}”</h2>
          <button type="button" className="share__close" onClick={onClose} aria-label="Close" disabled={busy}>
            <CloseMark size={10} />
          </button>
        </header>

        {done ? (
          <div className="share__done">
            {done.shared.length > 0 ? (
              <p>
                Shared with {done.shared.map((one) => one.name).join(', ')}. Each of them now has
                their own copy in their projects.
              </p>
            ) : null}
            {done.failed.length > 0 ? (
              <p className="share__bad">
                Not shared with {done.failed.map((one) => one.name).join(', ')}: {done.failed[0].reason}.
              </p>
            ) : null}
            <div className="share__foot">
              <button type="button" className="ww-btn ww-btn--primary ww-btn--sm" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="share__lead">
              Each person gets their own copy of this project as it is now — the canvas, its
              pictures and the conversation. What either of you does afterwards stays with you.
            </p>

            {chosen.length > 0 ? (
              <div className="share__chosen">
                {chosen.map((person) => (
                  <button
                    key={person.id}
                    type="button"
                    className="share__chip"
                    onClick={() => toggle(person)}
                    title="Remove"
                  >
                    {person.name}
                    <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
            ) : null}

            <input
              ref={input}
              className="share__search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find someone by name or email"
              aria-label="Find someone"
            />

            <ul className="share__list" role="listbox" aria-multiselectable="true">
              {found === null ? (
                <li className="share__quiet">Looking…</li>
              ) : found.length === 0 ? (
                <li className="share__quiet">
                  Nobody by that name. Only people who have signed in to WonderWorks at least once
                  can be found.
                </li>
              ) : (
                found.map((person) => {
                  const on = isChosen(person)
                  return (
                    <li key={person.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={on}
                        className={`share__person${on ? ' share__person--on' : ''}`}
                        onClick={() => toggle(person)}
                      >
                        <span className="share__tick" aria-hidden="true">
                          ✓
                        </span>
                        <span className="share__who">
                          <span className="share__name">{person.name}</span>
                          <span className="share__email">{person.email}</span>
                        </span>
                      </button>
                    </li>
                  )
                })
              )}
            </ul>

            {error ? <p className="share__bad">{error}</p> : null}

            <div className="share__foot">
              <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button
                type="button"
                className="ww-btn ww-btn--primary ww-btn--sm"
                disabled={busy || chosen.length === 0}
                onClick={() => void share()}
              >
                {busy
                  ? 'Copying…'
                  : chosen.length === 0
                    ? 'Choose someone'
                    : `Share with ${chosen.length} ${chosen.length === 1 ? 'person' : 'people'}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}

/** The share mark: a box with an arrow leaving it. */
export function ShareGlyph({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M7 1.8v7.2M4.3 4.3 7 1.6l2.7 2.7M3.2 6.6H2.6a.8.8 0 0 0-.8.8v4.2c0 .4.4.8.8.8h8.8c.4 0 .8-.4.8-.8V7.4a.8.8 0 0 0-.8-.8h-.6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
